import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { variableDeclarationsFingerprint } from "../../lib/variableCascade";
import {
  isPreviewable,
  toPreviewRequest,
  type SimulationLaunchState,
  type VariableDeclaration,
} from "./simulationLaunchState";

type PreviewRequest = components["schemas"]["BatchPreviewRequest"];
const PREVIEW_DEBOUNCE_MS = 500;

/** Launches and Templates preview the same current inputs, never a stale receipt. */
export function useBatchSpecPreview(
  state: SimulationLaunchState,
  variables: VariableDeclaration[],
  enabled = true,
) {
  const body =
    enabled && isPreviewable(state, variables)
      ? toPreviewRequest(state, variables)
      : null;
  const fingerprint =
    body === null
      ? null
      : JSON.stringify([body, variableDeclarationsFingerprint(variables)]);
  const latestBody = useRef(body);
  latestBody.current = body;
  const [request, setRequest] = useState<{
    body: PreviewRequest;
    fingerprint: string;
  } | null>(null);

  useEffect(() => {
    if (fingerprint === null) {
      setRequest(null);
      return;
    }
    const handle = window.setTimeout(() => {
      const current = latestBody.current;
      if (current !== null) setRequest({ body: current, fingerprint });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [fingerprint]);

  const isCurrent =
    fingerprint !== null && request?.fingerprint === fingerprint;
  const query = useQuery({
    queryKey: queryKeys.batches.preview(request?.fingerprint ?? ""),
    enabled: isCurrent,
    queryFn: async ({ signal }) => {
      if (request === null) throw new Error("Preview inputs are required.");
      const { data, error } = await apiClient.POST("/v1/batches:preview", {
        body: request.body,
        signal,
      });
      if (error) throw new ApiRequestError(error);
      return data;
    },
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });

  const { refetch } = query;
  useEffect(() => {
    if (!isCurrent) return;
    const refresh = () => {
      if (document.visibilityState === "visible") {
        void refetch({ cancelRefetch: false });
      }
    };
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
    };
  }, [isCurrent, refetch]);

  return {
    retry: () => {
      if (isCurrent) void refetch();
    },
    errorMessage:
      isCurrent && query.isError
        ? requestErrorDescription(query.error, "Preview failed.")
        : undefined,
    isPending:
      fingerprint !== null &&
      (!isCurrent || query.isPending || query.isFetching),
    preview:
      isCurrent && query.isSuccess && !query.isFetching
        ? query.data
        : undefined,
  };
}
